import { OpenMarketsError, parseErrorEnvelope } from "./errors.js";
import { truncateSample } from "./friction.js";
const DEFAULT_BASE = "https://api.openmarkets.ai/flow/v1";
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
function toNum(v) {
    if (v == null)
        return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}
function parseRateLimit(headers) {
    const rl = {
        limitMinute: toNum(headers.get("x-ratelimit-limit-minute")),
        remainingMinute: toNum(headers.get("x-ratelimit-remaining-minute")),
        resetMinute: toNum(headers.get("x-ratelimit-reset-minute")),
        limitDay: toNum(headers.get("x-ratelimit-limit-day")),
        remainingDay: toNum(headers.get("x-ratelimit-remaining-day")),
        resetDay: toNum(headers.get("x-ratelimit-reset-day")),
        retrievedAt: Date.now(),
    };
    const anyPresent = rl.limitMinute !== null ||
        rl.remainingMinute !== null ||
        rl.resetMinute !== null ||
        rl.limitDay !== null ||
        rl.remainingDay !== null ||
        rl.resetDay !== null;
    return anyPresent ? rl : null;
}
/**
 * Extract `{ data, pagination, meta }` from a raw response body. Defensive about
 * whether an endpoint wraps its payload: if the body is an object with a `data`
 * key, we unwrap; otherwise the body itself is treated as `data`. This matters
 * because API-NOTES documents `/auth/me` with an unwrapped example even though the
 * envelope section says every success is wrapped (logged as friction).
 */
function unwrapEnvelope(raw) {
    if (raw && typeof raw === "object" && !Array.isArray(raw) && "data" in raw) {
        const r = raw;
        const out = { data: r["data"] };
        if (r["pagination"] && typeof r["pagination"] === "object") {
            out.pagination = r["pagination"];
        }
        if (r["meta"] && typeof r["meta"] === "object") {
            out.meta = r["meta"];
        }
        return out;
    }
    return { data: raw };
}
export class HttpClient {
    baseUrl;
    /** Rate-limit snapshot from the most recent response (any status). */
    lastRateLimit = null;
    apiKey;
    fetchImpl;
    validate;
    onRateLimit;
    onSchemaMismatch;
    maxRetries;
    retryBaseMs;
    maxRetryDelayMs;
    constructor(opts) {
        if (!opts.apiKey)
            throw new Error("OpenMarkets client requires an apiKey");
        this.apiKey = opts.apiKey;
        this.baseUrl = (opts.baseUrl ?? DEFAULT_BASE).replace(/\/+$/, "");
        const f = opts.fetch ?? globalThis.fetch;
        if (!f) {
            throw new Error("No fetch implementation available; pass { fetch } explicitly");
        }
        this.fetchImpl = f;
        this.validate = opts.validate ?? "warn";
        this.onRateLimit = opts.onRateLimit;
        this.onSchemaMismatch = opts.onSchemaMismatch;
        this.maxRetries = opts.maxRetries ?? 3;
        this.retryBaseMs = opts.retryBaseMs ?? 250;
        this.maxRetryDelayMs = opts.maxRetryDelayMs ?? 10_000;
    }
    buildUrl(opts) {
        const path = opts.path.startsWith("/") ? opts.path : `/${opts.path}`;
        let url = this.baseUrl + path;
        if (opts.query) {
            const sp = new URLSearchParams();
            for (const [k, v] of Object.entries(opts.query)) {
                if (v === undefined || v === null)
                    continue;
                sp.set(k, String(v));
            }
            const qs = sp.toString();
            if (qs)
                url += `?${qs}`;
        }
        return url;
    }
    /** Milliseconds to wait before a retry. `attempt` is 0-based. */
    computeRetryDelay(status, attempt, err, retryAfterHeader) {
        if (status === 429) {
            let secs = null;
            if (retryAfterHeader) {
                const parsed = Number(retryAfterHeader);
                if (Number.isFinite(parsed))
                    secs = parsed;
            }
            if (secs === null && err)
                secs = err.retryAfterSeconds;
            const base = (secs ?? 1) * 1000;
            // Attempt multiplier: escalate on repeated 429s.
            return base * (attempt + 1);
        }
        // 500 / 503 / transport: exponential backoff with full jitter.
        const exp = Math.min(this.maxRetryDelayMs, this.retryBaseMs * 2 ** attempt);
        return Math.floor(Math.random() * exp);
    }
    async request(opts) {
        const url = this.buildUrl(opts);
        const headers = {
            "X-API-Key": this.apiKey,
            Accept: "application/json",
            ...(opts.headers ?? {}),
        };
        let bodyStr;
        if (opts.body !== undefined) {
            headers["Content-Type"] = "application/json";
            bodyStr = JSON.stringify(opts.body);
        }
        for (let attempt = 0;; attempt++) {
            let res;
            try {
                res = await this.fetchImpl(url, { method: opts.method, headers, body: bodyStr });
            }
            catch (cause) {
                // A caller-aborted request is final: retrying it would re-download what they cancelled.
                if (cause?.name === "AbortError")
                    throw cause;
                if (attempt < this.maxRetries) {
                    await sleep(this.computeRetryDelay(503, attempt, null, null));
                    continue;
                }
                throw OpenMarketsError.network(cause);
            }
            const rl = parseRateLimit(res.headers);
            if (rl) {
                this.lastRateLimit = rl;
                this.onRateLimit?.(rl);
            }
            const text = await res.text();
            let json;
            if (text) {
                try {
                    json = JSON.parse(text);
                }
                catch {
                    json = undefined; // non-JSON body (handled below by ok/!ok branches)
                }
            }
            if (!res.ok) {
                const err = parseErrorEnvelope(res.status, json, text);
                const retryable = res.status === 429 || res.status === 500 || res.status === 503;
                if (retryable && attempt < this.maxRetries) {
                    await sleep(this.computeRetryDelay(res.status, attempt, err, res.headers.get("retry-after")));
                    continue;
                }
                throw err;
            }
            const { data: rawData, pagination, meta } = unwrapEnvelope(json);
            const data = this.validateData(opts.endpoint, opts.dataSchema, rawData);
            const result = { data, rateLimit: rl ?? this.lastRateLimit };
            if (pagination)
                result.pagination = pagination;
            if (meta)
                result.meta = meta;
            return result;
        }
    }
    validateData(endpoint, schema, raw) {
        if (this.validate === "off")
            return raw;
        const parsed = schema.safeParse(raw);
        if (parsed.success)
            return parsed.data;
        if (this.validate === "strict") {
            throw new OpenMarketsError(`Schema validation failed for ${endpoint}`, {
                code: "schema_validation_failed",
                status: 0,
                details: { endpoint, issues: parsed.error.issues },
            });
        }
        // warn: report, but hand back the raw payload so callers aren't blocked.
        this.onSchemaMismatch?.(endpoint, parsed.error.issues, truncateSample(raw));
        return raw;
    }
}
//# sourceMappingURL=http.js.map